<template>
  <v-container fluid class="admin-page">
    <v-row class="align-center" justify="space-around">
      <v-col cols="6">
        <h2>Events</h2>
      </v-col>
      <v-col cols="6">
        <v-row>
          <v-col cols="8">
            <v-text-field
              v-model="search"
              label="Search event"
              append-icon="mdi-magnify"
              solo
              clearable
              dense
              @change="fetchEvents"
            ></v-text-field>
          </v-col>
          <v-col cols="2" class="fill-height">
            <v-btn @click.prevent="fetchEvents" :loading="loading">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-col>
          <v-col cols="2">
            <v-btn
              class="text--futura text--white text-capitalize"
              dark
              block
              @click="openCreateDialog"
            >
              Add
            </v-btn>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-row class="mt-2">
      <v-col cols="12">
        <Alert />
        <v-data-table
          :headers="headers"
          :items="events"
          :loading="loading"
          loading-text="Loading events..."
          :items-per-page="15"
          class="elevation-1"
        >
          <template v-slot:[`item.event_name`]="{ item }">
            <div>{{ item.event_name }}</div>
            <div class="grey--text text-caption">{{ item.id }}</div>
          </template>

          <template v-slot:[`item.started_at`]="{ item }">
            {{ formatDateTime(item.started_at) }}
          </template>

          <template v-slot:[`item.guest_summary`]="{ item }">
            <span>{{ item.guest_summary.total }}</span>
            <span class="grey--text text-caption ml-1">
              ({{ item.guest_summary.accepted }} yes /
              {{ item.guest_summary.rejected }} no /
              {{ item.guest_summary.pending }} pending)
            </span>
          </template>

          <template v-slot:[`item.allow_self_registration`]="{ item }">
            <v-icon small :color="item.allow_self_registration ? 'green' : 'grey'">
              {{ item.allow_self_registration ? "mdi-check-circle" : "mdi-minus-circle" }}
            </v-icon>
          </template>

          <template v-slot:[`item.paused`]="{ item }">
            <v-switch
              :input-value="item.paused"
              inset
              hide-details
              dense
              @change="togglePaused(item, $event)"
              :loading="Boolean(updatingPaused[item.id])"
              :disabled="Boolean(updatingPaused[item.id])"
            ></v-switch>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-btn icon small title="Guest list" @click="openGuests(item)">
              <v-icon small>mdi-account-multiple</v-icon>
            </v-btn>
            <v-btn icon small title="Edit" @click="openEditDialog(item)">
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
            <v-btn icon small title="Delete" @click="confirmDelete(item)">
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <v-dialog v-model="showFormDialog" max-width="900" scrollable>
      <v-card>
        <v-card-title class="text-h6">
          {{ isEditing ? "Edit Event" : "Add Event" }}
        </v-card-title>

        <v-card-text>
          <v-container fluid class="px-0">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.event_name"
                  label="Event Name"
                  dense
                  outlined
                  required
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.id"
                  label="Event ID (slug)"
                  dense
                  outlined
                  :disabled="isEditing"
                  :hint="idHint"
                  persistent-hint
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="4">
                <v-text-field
                  v-model="form.started_at"
                  label="Start"
                  type="datetime-local"
                  dense
                  outlined
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="4">
                <v-text-field
                  v-model="form.ended_at"
                  label="End"
                  type="datetime-local"
                  dense
                  outlined
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="4">
                <v-text-field
                  v-model="form.duration"
                  label="Duration (minutes)"
                  type="number"
                  dense
                  outlined
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.location"
                  label="Location"
                  dense
                  outlined
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.rsvp_base_url"
                  label="RSVP page base URL"
                  :hint="rsvpUrlHint"
                  persistent-hint
                  dense
                  outlined
                  clearable
                  :error-messages="rsvpBaseUrlError"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-textarea
                  v-model="form.event_details"
                  label="Event Details (shown to guests)"
                  dense
                  outlined
                  rows="3"
                ></v-textarea>
              </v-col>

              <v-col cols="12" md="6">
                <v-textarea
                  v-model="form.notes"
                  label="Internal Notes"
                  dense
                  outlined
                  rows="3"
                ></v-textarea>
              </v-col>

              <v-col cols="12" md="6">
                <v-switch
                  v-model="form.allow_self_registration"
                  inset
                  hide-details
                  label="Allow public self-registration"
                ></v-switch>
              </v-col>

              <v-col cols="12" md="6">
                <v-switch
                  v-model="form.paused"
                  inset
                  hide-details
                  label="Paused (closes RSVP and registration)"
                ></v-switch>
              </v-col>

              <v-col cols="12">
                <div class="d-flex align-center mt-4 mb-2">
                  <h3 class="text-subtitle-1 mb-0">RSVP Questions</h3>
                  <v-spacer></v-spacer>
                  <v-btn small text @click="addQuestion">
                    <v-icon small left>mdi-plus</v-icon> Add question
                  </v-btn>
                  <v-btn small text @click="showRawRules = !showRawRules">
                    {{ showRawRules ? "Hide JSON" : "Edit JSON" }}
                  </v-btn>
                </div>

                <v-alert dense text type="info" class="text-caption">
                  question_1 is the attendance question: answering it decides whether a
                  guest counts as accepted or rejected.
                </v-alert>

                <div v-if="!showRawRules">
                  <v-card
                    v-for="(question, index) in form.questions"
                    :key="question.key"
                    outlined
                    class="mb-3 pa-3"
                  >
                    <v-row dense>
                      <v-col cols="12" md="5">
                        <v-text-field
                          v-model="question.question_text"
                          :label="`${question.key} — question text`"
                          dense
                          outlined
                          hide-details
                        ></v-text-field>
                      </v-col>
                      <v-col cols="6" md="3">
                        <v-select
                          v-model="question.response_allowed"
                          :items="responseTypes"
                          label="Answer type"
                          dense
                          outlined
                          hide-details
                        ></v-select>
                      </v-col>
                      <v-col cols="6" md="2">
                        <v-text-field
                          v-model="question.linked_field"
                          label="Linked field"
                          hint="e.g. pax"
                          dense
                          outlined
                          hide-details
                        ></v-text-field>
                      </v-col>
                      <v-col cols="6" md="1" class="d-flex align-center">
                        <v-switch
                          v-model="question.required"
                          inset
                          dense
                          hide-details
                          label="Req."
                          class="mt-0"
                        ></v-switch>
                      </v-col>
                      <v-col cols="6" md="1" class="d-flex align-center justify-end">
                        <v-btn
                          icon
                          small
                          :disabled="index === 0"
                          title="Remove"
                          @click="removeQuestion(index)"
                        >
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-card>
                </div>

                <v-textarea
                  v-else
                  v-model="rawRules"
                  label="booking_rules (JSON)"
                  outlined
                  rows="12"
                  spellcheck="false"
                  class="rules-json"
                ></v-textarea>
              </v-col>

              <v-col cols="12">
                <h3 class="text-subtitle-1 mt-4 mb-2">WhatsApp Templates</h3>
                <v-alert dense text type="info" class="text-caption">
                  These are the defaults for every guest of this event. Map each
                  placeholder — <strong>{{ placeholderTag(0) }}</strong>,
                  <strong>{{ placeholderTag(1) }}</strong>, … — to the field that fills it, in
                  order. A guest can override any of these individually.
                </v-alert>

                <v-card
                  v-for="kind in messageKinds"
                  :key="kind"
                  outlined
                  class="mb-3 pa-3"
                >
                  <v-row dense>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.templates[kind].template_name"
                        :label="`${kindLabel(kind)} template name`"
                        hint="Exactly as registered with WhatsApp"
                        persistent-hint
                        dense
                        outlined
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="2">
                      <v-text-field
                        v-model="form.templates[kind].language_code"
                        label="Language"
                        placeholder="id"
                        dense
                        outlined
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4" class="d-flex align-center">
                      <v-btn
                        small
                        text
                        :disabled="!form.templates[kind].template_name"
                        @click="addVariable(kind)"
                      >
                        <v-icon small left>mdi-plus</v-icon> Add variable
                      </v-btn>
                    </v-col>

                    <v-col cols="12" md="3">
                      <v-select
                        v-model="form.templates[kind].header_type"
                        :items="headerTypeOptions"
                        label="Header media"
                        dense
                        outlined
                        clearable
                      ></v-select>
                    </v-col>
                    <v-col cols="12" :md="form.templates[kind].header_type === 'document' ? 5 : 7">
                      <v-text-field
                        v-model="form.templates[kind].header_media_url"
                        label="Header media URL"
                        :disabled="!form.templates[kind].header_type"
                        hint="Public http(s) link — WhatsApp fetches it directly"
                        persistent-hint
                        dense
                        outlined
                        clearable
                        :error-messages="headerMediaError(kind)"
                      ></v-text-field>
                    </v-col>
                    <v-col
                      v-if="form.templates[kind].header_type === 'document'"
                      cols="12"
                      md="2"
                    >
                      <v-text-field
                        v-model="form.templates[kind].header_filename"
                        label="Filename"
                        hint="Shown to the guest"
                        persistent-hint
                        dense
                        outlined
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="2" class="d-flex align-center justify-center">
                      <v-img
                        v-if="showHeaderThumbnail(kind)"
                        :src="form.templates[kind].header_media_url"
                        max-height="56"
                        max-width="100"
                        contain
                      >
                        <template v-slot:placeholder>
                          <v-icon small color="grey">mdi-image-off</v-icon>
                        </template>
                      </v-img>
                      <v-icon
                        v-else-if="form.templates[kind].header_type && !headerMediaError(kind)"
                        color="grey darken-1"
                      >
                        {{ headerTypeIcon(form.templates[kind].header_type) }}
                      </v-icon>
                    </v-col>

                    <v-col
                      v-for="(variable, index) in form.templates[kind].variables"
                      :key="`${kind}-${index}`"
                      cols="12"
                      md="6"
                    >
                      <div class="d-flex align-center">
                        <span class="mr-2 grey--text text-caption placeholder-tag">
                          {{ placeholderTag(index) }}
                        </span>
                        <v-select
                          v-model="form.templates[kind].variables[index]"
                          :items="messageFields"
                          item-text="label"
                          item-value="key"
                          label="Field"
                          dense
                          outlined
                          hide-details
                        ></v-select>
                        <v-btn icon small class="ml-1" @click="removeVariable(kind, index)">
                          <v-icon small>mdi-close</v-icon>
                        </v-btn>
                      </div>
                    </v-col>
                  </v-row>
                </v-card>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeFormDialog">Cancel</v-btn>
          <v-btn dark :loading="saving" @click="saveEvent">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import Alert from "@/components/Alert.vue";
import server from "../api";

const RESPONSE_TYPES = [
  { text: "Yes / No", value: "boolean" },
  { text: "Number", value: "number" },
  { text: "Text", value: "text" },
];

const MESSAGE_KINDS = ["invitation", "reminder", "qr"];

const HEADER_TYPE_OPTIONS = [
  { text: "None", value: null },
  { text: "Image", value: "image" },
  { text: "Video", value: "video" },
  { text: "Document", value: "document" },
];

const HEADER_TYPE_ICON = {
  image: "mdi-image",
  video: "mdi-video",
  document: "mdi-file-document",
};

const emptyTemplates = () =>
  MESSAGE_KINDS.reduce((templates, kind) => {
    templates[kind] = {
      template_name: "",
      language_code: "id",
      header_type: null,
      header_media_url: "",
      header_filename: "",
      variables: [],
    };
    return templates;
  }, {});

const createDefaultForm = () => ({
  id: "",
  event_name: "",
  started_at: "",
  ended_at: "",
  duration: "",
  location: "",
  rsvp_base_url: "",
  event_details: "",
  notes: "",
  allow_self_registration: false,
  paused: false,
  questions: [],
  templates: emptyTemplates(),
});

const defaultQuestion = () => ({
  key: "question_1",
  question_text: "Will you be attending?",
  question_type: "attending_event",
  response_allowed: "boolean",
  linked_field: "",
  required: true,
});

export default {
  name: "Events",
  components: {
    Alert,
  },
  data() {
    return {
      loading: false,
      saving: false,
      search: "",
      events: [],
      updatingPaused: {},
      showFormDialog: false,
      showRawRules: false,
      rawRules: "{}",
      editingId: null,
      responseTypes: RESPONSE_TYPES,
      messageKinds: MESSAGE_KINDS,
      messageFields: [],
      headerTypeOptions: HEADER_TYPE_OPTIONS,
      form: createDefaultForm(),
      headers: [
        { text: "Event", value: "event_name" },
        { text: "Start", value: "started_at" },
        { text: "Guests", value: "guest_summary", sortable: false },
        { text: "Self-reg", value: "allow_self_registration", sortable: false },
        { text: "Paused", value: "paused", sortable: false },
        { text: "", value: "actions", sortable: false, align: "end" },
      ],
    };
  },
  computed: {
    isEditing() {
      return Boolean(this.editingId);
    },
    idHint() {
      if (this.isEditing) return "The event id cannot be changed";
      return "Leave blank to generate one from the name";
    },
    rsvpBaseUrlError() {
      const value = String(this.form.rsvp_base_url || "").trim();
      if (!value) return "";

      try {
        const parsed = new URL(value);
        return ["http:", "https:"].includes(parsed.protocol) ? "" : "Must be an http(s) URL";
      } catch (error) {
        return "Not a valid URL";
      }
    },
    // Shows the actual link a guest would get, so the trailing-slash and
    // path questions answer themselves.
    rsvpUrlHint() {
      const base = String(this.form.rsvp_base_url || "")
        .trim()
        .replace(/\/+$/, "");
      if (!base || this.rsvpBaseUrlError) {
        return "Where guests open their invitation. Used by the RSVP link template variable.";
      }
      return `Guests receive ${base}/<invitation-id>`;
    },
  },
  methods: {
    // Built here rather than inline: a literal {{n}} in the template would be
    // parsed as a Vue interpolation.
    placeholderTag(index) {
      return `{{${index + 1}}}`;
    },
    kindLabel(kind) {
      if (kind === "qr") return "QR";
      return kind.charAt(0).toUpperCase() + kind.slice(1);
    },
    // The API owns the list of bindable fields, so the dropdowns cannot drift
    // from what the server will accept.
    fetchMessageFields() {
      return server
        .get("/events/message-fields", this.getAuthConfig())
        .then((response) => {
          this.messageFields = response.data.fields || [];
        })
        .catch(() => {
          this.messageFields = [];
        });
    },
    addVariable(kind) {
      const first = this.messageFields.length ? this.messageFields[0].key : "name";
      this.form.templates[kind].variables.push(first);
    },
    removeVariable(kind, index) {
      this.form.templates[kind].variables.splice(index, 1);
    },
    templatesFromEvent(messageTemplates) {
      const templates = emptyTemplates();
      const source = messageTemplates && typeof messageTemplates === "object" ? messageTemplates : {};

      MESSAGE_KINDS.forEach((kind) => {
        const config = source[kind];
        if (!config) return;
        templates[kind] = {
          template_name: config.template_name || "",
          language_code: config.language_code || "id",
          // header_image_url is the legacy alias for an image header.
          header_type: config.header_type || (config.header_image_url ? "image" : null),
          header_media_url: config.header_media_url || config.header_image_url || "",
          header_filename: config.header_filename || "",
          variables: Array.isArray(config.variables) ? [...config.variables] : [],
        };
      });

      return templates;
    },
    // Mirrors the server's rule, so a bad URL is caught in the form rather than
    // coming back as a 400 after Save.
    headerMediaError(kind) {
      const config = this.form.templates[kind];
      const value = String(config.header_media_url || "").trim();

      if (!config.header_type) return "";
      if (!value) return "Required for a media header";

      try {
        const parsed = new URL(value);
        return ["http:", "https:"].includes(parsed.protocol) ? "" : "Must be an http(s) URL";
      } catch (error) {
        return "Not a valid URL";
      }
    },
    showHeaderThumbnail(kind) {
      const config = this.form.templates[kind];
      return config.header_type === "image" && config.header_media_url && !this.headerMediaError(kind);
    },
    headerTypeIcon(headerType) {
      return HEADER_TYPE_ICON[headerType] || "mdi-paperclip";
    },
    // Kinds with no template name are omitted rather than sent as blanks, so
    // clearing the field removes the template instead of saving an empty one.
    templatesToPayload() {
      const payload = {};

      MESSAGE_KINDS.forEach((kind) => {
        const config = this.form.templates[kind];
        const name = String(config.template_name || "").trim();
        if (!name) return;

        payload[kind] = {
          template_name: name,
          language_code: String(config.language_code || "id").trim() || "id",
          header_type: config.header_type || null,
          header_media_url: config.header_type
            ? String(config.header_media_url || "").trim() || null
            : null,
          header_filename:
            config.header_type === "document"
              ? String(config.header_filename || "").trim() || null
              : null,
          variables: config.variables.filter(Boolean),
        };
      });

      return payload;
    },
    getAuthConfig(extra = {}) {
      return {
        ...extra,
        headers: {
          token: localStorage.token,
          ...(extra.headers || {}),
        },
      };
    },
    getErrorMessage(err, fallback) {
      return (
        (err.response &&
          err.response.data &&
          err.response.data.errors &&
          (Array.isArray(err.response.data.errors)
            ? err.response.data.errors[0].message
            : err.response.data.errors.message || err.response.data.errors)) ||
        (err.response && err.response.data && err.response.data.message) ||
        fallback
      );
    },
    formatDateTime(value) {
      if (!value) return "-";
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return "-";
      return parsed.toLocaleString();
    },
    // <input type="datetime-local"> needs a local "YYYY-MM-DDTHH:mm" string.
    toLocalInput(value) {
      if (!value) return "";
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return "";
      const offsetMs = parsed.getTimezoneOffset() * 60000;
      return new Date(parsed.getTime() - offsetMs).toISOString().slice(0, 16);
    },
    fetchEvents() {
      this.loading = true;

      const params = { per_page: 100 };
      if (this.search) params.q = this.search;

      server
        .get("/events", this.getAuthConfig({ params }))
        .then((response) => {
          this.events = (response.data.events || []).map((event) => ({
            ...event,
            guest_summary: event.guest_summary || {
              total: 0,
              accepted: 0,
              rejected: 0,
              pending: 0,
            },
          }));
        })
        .catch((err) => {
          this.$store.commit(
            "SET_ERROR",
            this.getErrorMessage(err, "Failed to load events")
          );
        })
        .finally(() => {
          this.loading = false;
        });
    },
    openGuests(item) {
      this.$router.push({ name: "EventGuests", params: { eventId: item.id } });
    },
    // The editor keeps questions as a list; booking_rules is a keyed object.
    questionsFromRules(bookingRules) {
      const rules = bookingRules && typeof bookingRules === "object" ? bookingRules : {};
      const keys = Object.keys(rules)
        .filter((key) => /^question_\d+$/.test(key))
        .sort((a, b) => Number(a.split("_")[1]) - Number(b.split("_")[1]));

      if (!keys.length) return [defaultQuestion()];

      return keys.map((key) => ({
        key,
        question_text: rules[key].question_text || "",
        question_type: rules[key].question_type || "",
        response_allowed: rules[key].response_allowed || "text",
        linked_field: rules[key].linked_field || "",
        required: rules[key].required !== false,
        extra: rules[key],
      }));
    },
    rulesFromQuestions(questions) {
      const rules = {};

      questions.forEach((question, index) => {
        const key = `question_${index + 1}`;
        // Keep any fields the JSON editor set that this form has no input for
        // (conditional, question_guideline, ...) instead of dropping them.
        const preserved = { ...(question.extra || {}) };
        delete preserved.question_text;
        delete preserved.question_type;
        delete preserved.response_allowed;
        delete preserved.linked_field;
        delete preserved.required;

        rules[key] = {
          ...preserved,
          question_text: question.question_text,
          response_allowed: question.response_allowed,
          required: Boolean(question.required),
        };

        if (question.question_type) {
          rules[key].question_type = question.question_type;
        }
        if (question.linked_field) {
          rules[key].linked_field = question.linked_field;
        }
      });

      return rules;
    },
    addQuestion() {
      this.form.questions.push({
        key: `question_${this.form.questions.length + 1}`,
        question_text: "",
        question_type: "",
        response_allowed: "text",
        linked_field: "",
        required: true,
      });
    },
    removeQuestion(index) {
      this.form.questions.splice(index, 1);
      this.form.questions.forEach((question, position) => {
        question.key = `question_${position + 1}`;
      });
    },
    openCreateDialog() {
      this.editingId = null;
      this.form = createDefaultForm();
      this.form.questions = [defaultQuestion()];
      this.rawRules = JSON.stringify(this.rulesFromQuestions(this.form.questions), null, 2);
      this.showRawRules = false;
      this.showFormDialog = true;
    },
    openEditDialog(item) {
      this.editingId = item.id;
      this.form = {
        id: item.id,
        event_name: item.event_name || "",
        started_at: this.toLocalInput(item.started_at),
        ended_at: this.toLocalInput(item.ended_at),
        duration: item.duration === null || item.duration === undefined ? "" : item.duration,
        location: item.location || "",
        rsvp_base_url: item.rsvp_base_url || "",
        event_details: item.event_details || "",
        notes: item.notes || "",
        allow_self_registration: Boolean(item.allow_self_registration),
        paused: Boolean(item.paused),
        questions: this.questionsFromRules(item.booking_rules),
        templates: this.templatesFromEvent(item.message_templates),
      };
      this.rawRules = JSON.stringify(item.booking_rules || {}, null, 2);
      this.showRawRules = false;
      this.showFormDialog = true;
    },
    closeFormDialog() {
      this.showFormDialog = false;
      this.editingId = null;
      this.form = createDefaultForm();
    },
    buildBookingRules() {
      if (this.showRawRules) {
        try {
          const parsed = JSON.parse(this.rawRules);
          if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
            throw new Error("booking_rules must be a JSON object");
          }
          return parsed;
        } catch (error) {
          this.$store.commit("SET_ERROR", `Invalid booking_rules JSON: ${error.message}`);
          return null;
        }
      }

      const blank = this.form.questions.find((question) => !question.question_text.trim());
      if (blank) {
        this.$store.commit("SET_ERROR", `${blank.key} needs question text`);
        return null;
      }

      return this.rulesFromQuestions(this.form.questions);
    },
    saveEvent() {
      if (!this.form.event_name.trim()) {
        this.$store.commit("SET_ERROR", "Event name is required");
        return;
      }

      const bookingRules = this.buildBookingRules();
      if (!bookingRules) return;

      if (this.rsvpBaseUrlError) {
        this.$store.commit("SET_ERROR", `RSVP page base URL: ${this.rsvpBaseUrlError}`);
        return;
      }

      const badHeader = MESSAGE_KINDS.find((kind) => this.headerMediaError(kind));
      if (badHeader) {
        this.$store.commit(
          "SET_ERROR",
          `${this.kindLabel(badHeader)} header media: ${this.headerMediaError(badHeader)}`
        );
        return;
      }

      const payload = {
        event_name: this.form.event_name.trim(),
        started_at: this.form.started_at ? new Date(this.form.started_at).toISOString() : null,
        ended_at: this.form.ended_at ? new Date(this.form.ended_at).toISOString() : null,
        duration: this.form.duration === "" ? null : Number(this.form.duration),
        location: this.form.location.trim(),
        rsvp_base_url: String(this.form.rsvp_base_url || "").trim() || null,
        event_details: this.form.event_details.trim(),
        notes: this.form.notes.trim(),
        allow_self_registration: this.form.allow_self_registration,
        paused: this.form.paused,
        booking_rules: bookingRules,
        message_templates: this.templatesToPayload(),
      };

      this.saving = true;

      const request = this.isEditing
        ? server.put(`/events/${this.editingId}`, payload, this.getAuthConfig())
        : server.post(
            "/events",
            { ...payload, id: this.form.id.trim() || undefined },
            this.getAuthConfig()
          );

      request
        .then(() => {
          this.$store.commit(
            "SET_SUCCESS",
            this.isEditing ? "Event updated" : "Event created"
          );
          this.closeFormDialog();
          this.fetchEvents();
        })
        .catch((err) => {
          this.$store.commit(
            "SET_ERROR",
            this.getErrorMessage(err, "Failed to save event")
          );
        })
        .finally(() => {
          this.saving = false;
        });
    },
    togglePaused(item, value) {
      const previousPaused = item.paused;
      const nextPaused = value === true;

      this.$set(this.updatingPaused, item.id, true);
      item.paused = nextPaused;

      server
        .put(`/events/${item.id}`, { paused: nextPaused }, this.getAuthConfig())
        .then(() => {
          this.$store.commit(
            "SET_SUCCESS",
            `${item.event_name} is now ${nextPaused ? "paused" : "active"}`
          );
        })
        .catch((err) => {
          item.paused = previousPaused;
          this.$store.commit(
            "SET_ERROR",
            this.getErrorMessage(err, "Failed to update event")
          );
        })
        .finally(() => {
          this.$delete(this.updatingPaused, item.id);
        });
    },
    confirmDelete(item) {
      const guestCount = item.guest_summary ? item.guest_summary.total : 0;
      const warning = guestCount
        ? ` This also removes ${guestCount} guest(s) and their responses.`
        : "";

      if (!window.confirm(`Delete "${item.event_name}"?${warning}`)) return;

      server
        .delete(`/events/${item.id}`, this.getAuthConfig())
        .then(() => {
          this.$store.commit("SET_SUCCESS", "Event deleted");
          this.fetchEvents();
        })
        .catch((err) => {
          this.$store.commit(
            "SET_ERROR",
            this.getErrorMessage(err, "Failed to delete event")
          );
        });
    },
  },
  created() {
    this.fetchEvents();
    this.fetchMessageFields();
  },
};
</script>

<style scoped>
.rules-json >>> textarea {
  font-family: "Menlo", "Consolas", monospace;
  font-size: 12px;
  line-height: 1.5;
}

.placeholder-tag {
  font-family: "Menlo", "Consolas", monospace;
  white-space: nowrap;
}
</style>
